import { getFlags } from '@/feature-flags';
import type { AlertData, ClaimEventStepData } from '@/types';
import claimsData from '@data/claims';
import AirplaneIcon from '@icons-shared/newClaim/airplane.svg?react';
import CarIcon from '@icons-shared/newClaim/car.svg?react';
import LuggageIcon from '@icons-shared/newClaim/luggage.svg?react';
import MedicIcon from '@icons-shared/newClaim/medic.svg?react';
import WeatherIcon from '@icons-shared/newClaim/weather.svg?react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS, ROUTES } from '../../../../../config';

const { canMakeNewClaimInPortal, canMakeTravelEventClaims } = getFlags();

const claimNotAllowedInPortalAlertData: AlertData = {
  title: "We're taking you to a different claims platform",
  description: (
    <>
      We're in the middle of improving our claims experience. That means some claim types are
      handled on our previous platform for now. Don't worry, this won't affect your claim(s). Thanks
      for understanding!
    </>
  ),
};

const claimTravelEventTypeAlertData: AlertData = {
  description: (
    <>
      <p>Select this option if you or someone you were travelling with:</p>
      <ul>
        <li>
          experienced a disruption to your travel plans, including a trip cancellation, delay, or
          change, and
        </li>
        <li>
          you are claiming cancellation, additional or other travel-related expenses as a direct
          result of this disruption.
        </li>
      </ul>
    </>
  ),
};

const claimMedicalEventTypeAlertData: AlertData = {
  description: (
    <>
      <p>Select this option if you or someone you were travelling with:</p>
      <ul>
        <li>experienced an illness, injury or dental emergency before or during your trip, and</li>
        <li>
          you are claiming medical, cancellation or additional expenses as a direct result of this
          incident.
        </li>
      </ul>
      <p>
        <strong>Important:</strong> You may need to submit a medical report for some medical claims,
        depending on what you tell us and the diagnoses. If that's the case, we'll let you know.
        Want more information on this and why we might need them? Read about the{' '}
        <Link to={`${ROUTE_PATHS[ROUTES.CLAIMS]}#${claimsData.documentsYouNeed.header.id}`}>
          <strong>documents you'll need to support your claim</strong>
        </Link>
        .
      </p>
    </>
  ),
};

const claimEventStepData: ClaimEventStepData = {
  id: 'claim-event-step-section',
  testId: 'claim-event-step-section',
  title: 'Tell us what happened',
  description: 'Select the option that best describes the incident.',
  data: [
    {
      value: 'travelDisruption',
      label: 'Travel disruption',
      icon: AirplaneIcon,
      description: 'Claims involving cancellations, delays or changes to your trip.',
      alert:
        canMakeNewClaimInPortal && canMakeTravelEventClaims
          ? claimTravelEventTypeAlertData
          : claimNotAllowedInPortalAlertData,
    },
    {
      value: 'medical',
      label: 'Medical',
      icon: MedicIcon,
      description: 'Claims involving illness, injury, or dental emergencies.',
      alert: canMakeNewClaimInPortal
        ? claimMedicalEventTypeAlertData
        : claimNotAllowedInPortalAlertData,
    },
    {
      value: 'luggage',
      label: 'Luggage',
      icon: LuggageIcon,
      description: 'Claims involving lost, stolen, delayed, or damaged luggage.',
      alert: claimNotAllowedInPortalAlertData,
    },
    {
      value: 'rentalVehicle',
      label: 'Rental vehicle',
      icon: CarIcon,
      description: 'Claims involving your vehicle, or a rental vehicle issue.',
      alert: claimNotAllowedInPortalAlertData,
    },
    {
      value: 'anyOtherEvent',
      label: 'Any other event',
      icon: WeatherIcon,
      description: 'Claims involving anything else.',
      alert: claimNotAllowedInPortalAlertData,
    },
  ],
};

export default claimEventStepData;
